/**
 * Drives the step-by-step animated build: replays a pre-computed plan (one
 * RevealStep per checklist row, each already carrying its post-step
 * nodes/edges) onto the canvas over time via setNodes/setEdges, rather than
 * applying the whole plan atomically. Computing the full plan once (through
 * Gate-2 validation, in WorkflowAgentChatPanel.tsx) and only replaying
 * already-resolved snapshots here is what keeps node ids stable across the
 * reveal -- re-running applyGraphOps on growing op prefixes would mint a
 * fresh uuid per node each time.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getNodesBounds,
  getViewportForBounds,
  type FitBounds,
} from '@xyflow/react';
import { calculateGraphLayout } from '../graphLayout';
import { getLBNodeInfo } from '../nodes/registry';
import {
  DEFAULT_ZOOM_LEVEL,
  FIT_VIEW_PADDING,
  NODE_HEIGHT,
  NODE_WIDTH,
} from '../styles';
import { OutputType } from '../types';
import type {
  LBNodeDataWithIndexer,
  ReactFlowEdge,
  ReactFlowNode,
} from '../types';

function isSubgraphParent(node: ReactFlowNode): boolean {
  return (
    getLBNodeInfo(node.data as never)?.outputType === OutputType.SUBGRAPH_PARENT
  );
}

// Small plans should visibly build from the top down instead of reading as an
// atomic canvas update. Latency work should make this reveal START earlier,
// not compress the deliberate node -> edge -> node presentation. Large plans
// still compress so the reveal remains bounded.
const BASE_STEP_DELAY_MS = 900;
const MIN_STEP_DELAY_MS = 180;
const MAX_TOTAL_DURATION_MS = 12000;
const EDGE_TO_NODE_DELAY_MS = 360;
const FINAL_ANIMATION_HOLD_MS = 360;
const CAMERA_PRE_ROLL_MS = 250;

export function planRevealStepDelayMs(stepCount: number): number {
  if (stepCount <= 1) return BASE_STEP_DELAY_MS;
  const compressed = MAX_TOTAL_DURATION_MS / stepCount;
  return Math.max(MIN_STEP_DELAY_MS, Math.min(BASE_STEP_DELAY_MS, compressed));
}

/** React Flow's initial auto-fit must stay off only after the reveal has
 * successfully acquired the camera. If the pre-roll is unavailable or fails,
 * leaving auto-fit enabled is the safety fallback that keeps nodes visible. */
export function shouldUseReactFlowAutoFit(cameraIsFramed: boolean): boolean {
  return !cameraIsFramed;
}

export function getPlanRevealViewport(
  bounds: Parameters<FitBounds>[0],
  width: number,
  height: number,
  minZoom: number,
  maxZoom: number,
  padding: number
) {
  return getViewportForBounds(
    bounds,
    width,
    height,
    minZoom,
    Math.min(maxZoom, DEFAULT_ZOOM_LEVEL),
    padding
  );
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

// @xyflow/react's getNodesBounds, called without a live nodeLookup (the
// nodes here aren't mounted on canvas yet), sizes each node from a
// top-level width/height field it reads directly off the plain object
// (node.measured?.width ?? node.width ?? node.initialWidth ?? 0) --
// calculateGraphLayout's output never carries one (ordinary nodes are
// CSS-sized; SUBGRAPH_PARENT nodes get theirs nested under data.width/
// data.height), so every node would otherwise silently size to 0.
function withExplicitSize(node: ReactFlowNode): ReactFlowNode {
  if (isSubgraphParent(node)) {
    return {
      ...node,
      width: (node.data.width as number | undefined) ?? NODE_WIDTH,
      height: (node.data.height as number | undefined) ?? NODE_HEIGHT,
    };
  }
  return { ...node, width: NODE_WIDTH, height: NODE_HEIGHT };
}

/** A single checklist row's raw, unlaidout node/edge snapshot -- straight
 * from applyGraphOps({recordSteps: true})'s per-op OpStepResult, grouped
 * into rows by planPhases.groupOpsIntoSteps. Every node in here still has
 * whatever position createNode gave it (0, 0); start() freezes real
 * positions in before revealing anything. */
export interface RevealStepInput {
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
  /** Ids newly added or visibly reconfigured by this step -- what the CSS
   * enter animation applies its transient class to. */
  enteringNodeIds: string[];
  enteringEdgeIds: string[];
  /** True if this is the last step of its checkpoint phase -- fires
   * onPhaseComplete once applied. */
  isPhaseBoundary: boolean;
  phaseIndex: number;
}

/** Same shape as RevealStepInput -- distinguished only to make clear, at
 * the type level, that freezeStepsToFinalLayout has already run. */
type RevealStep = RevealStepInput;

/** Runs calculateGraphLayout once on the complete final graph, then remaps
 * every step's nodes to that layout's positions (and, for SUBGRAPH_PARENT
 * nodes, its computed data.width/data.height) -- never each step's own
 * layout. Recomputing layout at every step would fight the animation: a
 * node placed early would jump sideways as later branches fan out, and a
 * Loop node would visibly grow as its subgraph fills in. Node *data*
 * otherwise stays exactly what that step recorded (e.g. an unconfigured
 * node still reads as unconfigured until its own configure_node step) --
 * only geometry is frozen to the end state. */
function freezeStepsToFinalLayout(steps: RevealStepInput[]): RevealStep[] {
  const lastStep = steps[steps.length - 1];
  const finalLayoutNodes = calculateGraphLayout(lastStep.nodes, lastStep.edges);
  const finalPositionById = new Map(
    finalLayoutNodes.map((n) => [n.id, n.position])
  );
  const finalSizeById = new Map(
    finalLayoutNodes.filter(isSubgraphParent).map((n) => [
      n.id,
      {
        width: n.data.width as number,
        height: n.data.height as number,
      },
    ])
  );

  return steps.map((step) => ({
    ...step,
    nodes: step.nodes.map((node) => {
      const position = finalPositionById.get(node.id) ?? node.position;
      const size = finalSizeById.get(node.id);
      return {
        ...node,
        position,
        data: size
          ? (Object.freeze({
              ...node.data,
              width: size.width,
              height: size.height,
            }) as LBNodeDataWithIndexer)
          : node.data,
      };
    }),
  }));
}

interface StartArgs {
  /** Raw, unlaidout per-op-group snapshots -- start() freezes these to the
   * final layout before revealing anything (see freezeStepsToFinalLayout). */
  steps: RevealStepInput[];
  /** Called synchronously right after a step's nodes/edges are applied, so
   * the chat panel can tick that row's checkmark. */
  onStepApplied: (stepIndex: number) => void;
  /** Called right after a phase-boundary step is applied, with that step's
   * exact (not possibly-stale) arrays -- e.g. for saving a checkpoint. */
  onPhaseComplete: (
    phaseIndex: number,
    nodes: ReactFlowNode[],
    edges: ReactFlowEdge[]
  ) => void;
}

interface CancelArgs {
  /** Jump the canvas straight to the reveal's own final state. False for
   * Revert, which is about to overwrite the canvas with a checkpoint
   * snapshot instead and doesn't want this reveal's end state to flash in
   * first. */
  flushToFinal: boolean;
}

export interface UsePlanRevealArgs {
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
  setNodes: (nodes: ReactFlowNode[]) => void;
  setEdges: (edges: ReactFlowEdge[]) => void;
  /** Same saveCheckpoint useUndoRedo exposes -- call once before applying,
   * so the whole reveal is one undo step, exactly like handleApplyPlan. */
  saveCheckpoint: (nodes: ReactFlowNode[], edges: ReactFlowEdge[]) => void;
  isAgentPanelOpen: boolean;
  /** Real DOM width of the open chat panel, read fresh (via
   * getBoundingClientRect) at the moment the camera moves -- never a
   * hardcoded guess, since the panel's CSS is `min(420px, 46vw)`. */
  getAgentPanelWidth: () => number;
  /** Set by FitViewRefSetter -- fitBounds takes a caller-supplied
   * rectangle and never touches the live node lookup, unlike fitView. */
  fitBoundsRef: React.RefObject<FitBounds | null>;
}

export interface UsePlanRevealResult {
  isRevealing: boolean;
  /** True only after the completed-graph pre-roll has successfully acquired
   * the viewport. LBCanvas uses this narrower signal (not isRevealing) to
   * suppress React Flow's competing partial-graph auto-fit. */
  isCameraFramed: boolean;
  /** Ids the current (or most recently applied) step just added or visibly
   * reconfigured -- what LBCanvas.tsx applies the CSS enter class to. Empty
   * whenever isRevealing is false. */
  enteringNodeIds: ReadonlySet<string>;
  enteringEdgeIds: ReadonlySet<string>;
  /** Nodes mounted only to give React Flow an endpoint for an edge that is
   * revealed first. LBCanvas keeps these wrappers transparent until the
   * edge-to-node delay elapses. */
  stagedNodeIds: ReadonlySet<string>;
  start: (args: StartArgs) => Promise<void>;
  cancel: (args: CancelArgs) => void;
}

const EMPTY_ID_SET: ReadonlySet<string> = new Set();

interface ActiveReveal {
  steps: RevealStep[];
  cancelled: boolean;
  resolveWait: (() => void) | null;
}

export function usePlanReveal({
  nodes,
  edges,
  setNodes,
  setEdges,
  saveCheckpoint,
  isAgentPanelOpen,
  getAgentPanelWidth,
  fitBoundsRef,
}: UsePlanRevealArgs): UsePlanRevealResult {
  const [isRevealing, setIsRevealing] = useState(false);
  const [isCameraFramed, setIsCameraFramed] = useState(false);
  const [enteringIds, setEnteringIds] = useState<{
    nodeIds: ReadonlySet<string>;
    edgeIds: ReadonlySet<string>;
    stagedNodeIds: ReadonlySet<string>;
  }>({
    nodeIds: EMPTY_ID_SET,
    edgeIds: EMPTY_ID_SET,
    stagedNodeIds: EMPTY_ID_SET,
  });

  // Stable refs so start()/cancel() always read the live canvas state, not
  // a stale closure from whenever the hook was last re-rendered.
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const edgesRef = useRef(edges);
  edgesRef.current = edges;

  const activeRef = useRef<ActiveReveal | null>(null);

  const moveCamera = useCallback(
    async (visibleNodes: ReactFlowNode[], duration: number) => {
      const fitBounds = fitBoundsRef.current;
      if (!fitBounds || visibleNodes.length === 0) return false;
      const bounds = getNodesBounds(visibleNodes.map(withExplicitSize));
      const reservedLeft = isAgentPanelOpen ? getAgentPanelWidth() : 0;
      try {
        return await fitBounds(
          {
            x: bounds.x - reservedLeft,
            y: bounds.y,
            width: bounds.width + reservedLeft,
            height: bounds.height,
          },
          {
            duration,
            padding: FIT_VIEW_PADDING,
          }
        );
      } catch {
        // Camera animation failure must never block graph construction.
        return false;
      }
    },
    [fitBoundsRef, isAgentPanelOpen, getAgentPanelWidth]
  );

  const cancel = useCallback(
    ({ flushToFinal }: CancelArgs) => {
      const active = activeRef.current;
      if (!active) return;
      active.cancelled = true;
      if (flushToFinal) {
        const lastStep = active.steps[active.steps.length - 1];
        if (lastStep) {
          setNodes(lastStep.nodes);
          setEdges(lastStep.edges);
        }
      }
      // Wake up the in-flight per-step wait, if any, so start()'s loop
      // observes `cancelled` on its next check instead of sitting through
      // the rest of the delay.
      active.resolveWait?.();
    },
    [setNodes, setEdges]
  );

  // Any reveal still in flight when this hook unmounts (workflow switch,
  // panel's own unmount path) always flushes to the final state first,
  // same as closing the panel mid-reveal -- never leaves a half-built
  // graph on screen.
  useEffect(() => {
    return () => cancel({ flushToFinal: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = useCallback(
    async ({ steps: rawSteps, onStepApplied, onPhaseComplete }: StartArgs) => {
      if (rawSteps.length === 0 || activeRef.current) return;
      const steps = freezeStepsToFinalLayout(rawSteps);

      saveCheckpoint(nodesRef.current, edgesRef.current);
      setIsCameraFramed(false);
      setIsRevealing(true);

      const active: ActiveReveal = {
        steps,
        cancelled: false,
        resolveWait: null,
      };
      activeRef.current = active;

      const reduced = prefersReducedMotion();
      const delay = planRevealStepDelayMs(steps.length);
      const cameraDuration = reduced ? 0 : CAMERA_PRE_ROLL_MS;
      const initialNodeIds = new Set(nodesRef.current.map((node) => node.id));
      const finalNodes = steps[steps.length - 1].nodes;
      const finalNodeIds = new Set(finalNodes.map((node) => node.id));
      const revealChangesNodeSet =
        finalNodeIds.size !== initialNodeIds.size ||
        [...finalNodeIds].some((nodeId) => !initialNodeIds.has(nodeId));

      const waitForReveal = async (durationMs: number) => {
        if (durationMs <= 0) return;
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, durationMs);
          active.resolveWait = () => {
            clearTimeout(timer);
            resolve();
          };
        });
        active.resolveWait = null;
      };

      try {
        // Move to the completed graph's footprint before mounting the first
        // reveal snapshot. Awaiting fitBounds makes this a real pre-roll:
        // nodes cannot appear while the camera is still zoomed in.
        if (revealChangesNodeSet) {
          const cameraMoved = await moveCamera(finalNodes, cameraDuration);
          if (active.cancelled) return;
          setIsCameraFramed(cameraMoved);
        }

        for (let i = 0; i < steps.length; i++) {
          if (active.cancelled) return;

          const step = steps[i];
          const enteringNodeIds = new Set(step.enteringNodeIds);
          const enteringEdgeIds = new Set(step.enteringEdgeIds);
          const shouldStageNode =
            !reduced && enteringNodeIds.size > 0 && enteringEdgeIds.size > 0;
          const edgeToNodeDelay = shouldStageNode
            ? Math.min(EDGE_TO_NODE_DELAY_MS, delay)
            : 0;

          // React Flow needs both endpoints mounted before it can draw an
          // edge. Mount the target node transparently, reveal the edge toward
          // it, then make the node visible on the next render.
          setNodes(step.nodes);
          setEdges(step.edges);
          setEnteringIds({
            nodeIds: shouldStageNode ? EMPTY_ID_SET : enteringNodeIds,
            edgeIds: enteringEdgeIds,
            stagedNodeIds: shouldStageNode ? enteringNodeIds : EMPTY_ID_SET,
          });

          if (shouldStageNode) {
            await waitForReveal(edgeToNodeDelay);
            if (active.cancelled) return;

            setEnteringIds({
              nodeIds: enteringNodeIds,
              edgeIds: EMPTY_ID_SET,
              stagedNodeIds: EMPTY_ID_SET,
            });
          }

          // A step is complete only after its full snapshot is visible, so
          // checkpoints never capture the transparent staged-node UI.
          onStepApplied(i);
          if (step.isPhaseBoundary) {
            onPhaseComplete(step.phaseIndex, step.nodes, step.edges);
          }

          const isLastStep = i === steps.length - 1;
          if (!reduced) {
            await waitForReveal(
              isLastStep
                ? FINAL_ANIMATION_HOLD_MS
                : Math.max(0, delay - edgeToNodeDelay)
            );
          }
        }
      } finally {
        activeRef.current = null;
        setIsCameraFramed(false);
        setIsRevealing(false);
        setEnteringIds({
          nodeIds: EMPTY_ID_SET,
          edgeIds: EMPTY_ID_SET,
          stagedNodeIds: EMPTY_ID_SET,
        });
      }
    },
    [saveCheckpoint, setNodes, setEdges, moveCamera]
  );

  return {
    isRevealing,
    isCameraFramed,
    enteringNodeIds: enteringIds.nodeIds,
    enteringEdgeIds: enteringIds.edgeIds,
    stagedNodeIds: enteringIds.stagedNodeIds,
    start,
    cancel,
  };
}
